import { watchlistService } from "app/services/watchlist.service";
import useWatchlist from "features/watchlist/common/useWatchlist";
import { useEffect, useMemo, useState } from "react";
import { displayResultMessage } from "utils/misc";
import { sortByStringProperty } from "@shared/utils/general";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const useWatchlistModal = (
  symbol: string | undefined,
  onClose: () => void,
  isModalVisible: boolean
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.WATCHLIST);
  const { getSymbolExchanges, getExchangeToSymbolsStatus } =
    useSymbolsMetadata();

  const { watchlist } = useWatchlist(!isModalVisible);

  const [updateWatchlist, { isLoading }] =
    watchlistService.useUpdateWatchlistMutation();

  const [selectedExchanges, setSelectedExchanges] = useState<string[]>();
  const [isExchangesEmptyError, setIsExchangesEmptyError] = useState(false);
  const [valueChanged, setValueChanged] = useState(false);

  const symbolExchangesInWatchlist = useMemo(() => {
    return watchlist?.watchlist
      .filter((market) => market.symbol === symbol)
      .map((market) => market.exchange);
  }, [watchlist, symbol]);

  const symbolExchanges = useMemo(
    () => getSymbolExchanges(symbol),
    [getSymbolExchanges, symbol]
  );

  const showEmpty =
    (getExchangeToSymbolsStatus.isSuccess && !symbolExchanges?.length) ||
    getExchangeToSymbolsStatus.isError;
  const showExchangeList = !!symbolExchanges?.length;

  const sortedExchanges = showExchangeList
    ? [...symbolExchanges].sort((a, b) => sortByStringProperty(a, b))
    : [];

  const numSelectedExchanges = selectedExchanges?.length;

  useEffect(() => {
    if (
      isModalVisible &&
      symbolExchangesInWatchlist?.length &&
      !selectedExchanges
    ) {
      setSelectedExchanges(symbolExchangesInWatchlist);
    }
  }, [symbolExchangesInWatchlist, selectedExchanges, isModalVisible]);

  const getOkButtonText = () => {
    let buttonText = t("modal.add.add");
    if (symbolExchangesInWatchlist && selectedExchanges) {
      if (symbolExchangesInWatchlist.length > 0) {
        buttonText = t("modal.add.update");
        if (valueChanged && !selectedExchanges?.length)
          buttonText = t("modal.add.remove");
      }
    }
    return buttonText;
  };

  const updateWatchlistHandler = () => {
    if (
      !symbol ||
      (!symbolExchangesInWatchlist?.length && !selectedExchanges?.length)
    ) {
      setIsExchangesEmptyError(true);
      return;
    }

    if (selectedExchanges) {
      const updateWatchlistRequest = {
        symbol: symbol,
        exchanges: selectedExchanges,
      };
      displayResultMessage(updateWatchlist(updateWatchlistRequest).unwrap())
        .then(() => onCloseModal())
        .catch(() => {});
    }
  };

  const clearAll = () => {
    setValueChanged(true);
    setSelectedExchanges([]);
  };

  const selectAll = () => {
    setSelectedExchanges(sortedExchanges);
  };

  const onCloseModal = () => {
    onClose();
    setSelectedExchanges(undefined);
  };

  return {
    getExchangeToSymbolsStatus,
    setValueChanged,
    selectedExchanges,
    setSelectedExchanges,
    isExchangesEmptyError,
    setIsExchangesEmptyError,
    showEmpty,
    showExchangeList,
    onCloseModal,
    getOkButtonText,
    updateWatchlistHandler,
    isLoading,
    clearAll,
    selectAll,
    sortedExchanges,
    numSelectedExchanges,
  };
};

export default useWatchlistModal;
